import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Upload, RotateCcw, Loader2, ExternalLink, Image as ImageIcon, Type, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useSiteImages } from "@/context/SiteImagesContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { ICON_NAMES, getIcon } from "@/lib/icons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { refresh: refreshImages } = useSiteImages();
  const { refresh: refreshContent } = useSiteContent();
  const [tab, setTab] = useState("images");
  const [images, setImages] = useState([]);
  const [fields, setFields] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [savingText, setSavingText] = useState(false);
  const [upscaleFlags, setUpscaleFlags] = useState({});
  const [textLang, setTextLang] = useState("base");
  const [contentLangs, setContentLangs] = useState([{ code: "base", name: "Original" }]);

  const buildDraft = (flds, lang) => {
    const d = {};
    flds.forEach((f) => {
      d[f.key] = lang === "base" ? f.value : (f.translations?.[lang] ?? "");
    });
    return d;
  };

  const token = localStorage.getItem("admin_token");
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const resolve = (u) => (u.startsWith("/api/") ? `${BACKEND}${u}` : u);
      const [imgRes, contentRes] = await Promise.all([
        axios.get(`${API}/site-images`),
        axios.get(`${API}/site-content`, { params: { admin_view: true } }),
      ]);
      setImages(imgRes.data.map((i) => ({ ...i, url: resolve(i.url) })));
      const flds = contentRes.data.fields || [];
      setFields(flds);
      setContentLangs(contentRes.data.languages || [{ code: "base", name: "Original" }]);
      setDraft(buildDraft(flds, textLang));
    } catch (e) {
      toast.error("No se pudo cargar el contenido");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) { navigate("/admin/login"); return; }
    axios.get(`${API}/auth/me`, authHeader).catch(() => {
      localStorage.removeItem("admin_token");
      navigate("/admin/login");
    });
    load();
  }, []); // eslint-disable-line

  const logout = () => { localStorage.removeItem("admin_token"); navigate("/admin/login"); };

  const onUpload = async (key, file) => {
    if (!file) return;
    setBusyKey(key);
    const form = new FormData();
    form.append("key", key);
    form.append("file", file);
    if (upscaleFlags[key]) form.append("upscale", "true");
    try {
      await axios.post(`${API}/admin/images/upload`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Imagen actualizada");
      await load();
      await refreshImages();
    } catch (err) {
      if (err?.response?.status === 401) { logout(); return; }
      toast.error(err?.response?.data?.detail || "Error al subir la imagen");
    } finally { setBusyKey(null); }
  };

  const onResetImage = async (key) => {
    setBusyKey(key);
    const form = new FormData();
    form.append("key", key);
    try {
      await axios.post(`${API}/admin/images/reset`, form, authHeader);
      toast.success("Imagen restaurada");
      await load();
      await refreshImages();
    } catch (err) { toast.error("No se pudo restaurar"); }
    finally { setBusyKey(null); }
  };

  const saveTexts = async () => {
    setSavingText(true);
    try {
      await axios.post(`${API}/admin/content`, { updates: draft, lang: textLang }, authHeader);
      toast.success("Textos guardados");
      await load();
      await refreshContent();
    } catch (err) {
      if (err?.response?.status === 401) { logout(); return; }
      toast.error("No se pudieron guardar los textos");
    } finally { setSavingText(false); }
  };

  const changeTextLang = (lang) => {
    setTextLang(lang);
    setDraft(buildDraft(fields, lang));
  };

  const resetText = async (key) => {
    const form = new FormData();
    form.append("key", key);
    try {
      const { data } = await axios.post(`${API}/admin/content/reset`, form, authHeader);
      setDraft((d) => ({ ...d, [key]: data.value }));
      toast.success("Texto restaurado");
      await refreshContent();
    } catch (err) { toast.error("No se pudo restaurar"); }
  };

  const groups = [...new Set(fields.map((f) => f.group))];

  return (
    <div className="min-h-screen bg-[#050507] text-white" data-testid="admin-dashboard">
      <header className="glass sticky top-0 z-10 border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Logo Iris Photo" className="h-8 w-8 rounded-lg object-cover" />
            <span className="font-semibold tracking-tight">Administración de la web</span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5">
              Ver web <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <Button data-testid="admin-logout-button" onClick={logout} variant="ghost" className="text-white/80 hover:text-white hover:bg-white/5 gap-2">
              <LogOut className="h-4 w-4" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Tabs */}
        <div className="flex gap-2 p-1 rounded-full glass w-fit mb-8">
          <button
            data-testid="tab-images"
            onClick={() => setTab("images")}
            className={`px-5 h-10 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${tab === "images" ? "bg-gradient-to-r from-indigo-500 to-violet-500 text-white" : "text-white/60 hover:text-white"}`}
          >
            <ImageIcon className="h-4 w-4" /> Imágenes
          </button>
          <button
            data-testid="tab-texts"
            onClick={() => setTab("texts")}
            className={`px-5 h-10 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${tab === "texts" ? "bg-gradient-to-r from-indigo-500 to-violet-500 text-white" : "text-white/60 hover:text-white"}`}
          >
            <Type className="h-4 w-4" /> Textos
          </button>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-white/40" /></div>
        ) : tab === "images" ? (
          <>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Imágenes de la web</h1>
            <p className="text-white/55 mt-2">Sube una imagen nueva (JPG, PNG o WEBP) para reemplazar cualquiera. Sin límite de peso. Los cambios se ven al instante en la web.</p>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {images.map((img) => (
                <div key={img.key} className="glass rounded-2xl overflow-hidden flex flex-col" data-testid={`admin-image-${img.key}`}>
                  <div className="aspect-video bg-black/40 overflow-hidden relative">
                    <img src={img.url} alt={img.label} className="h-full w-full object-cover" />
                    {busyKey === img.key && (
                      <div className="absolute inset-0 bg-black/60 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <span className="text-xs uppercase tracking-wider text-indigo-400">{img.group}</span>
                    <h3 className="font-semibold mt-1 leading-snug">{img.label}</h3>
                    <label className="flex items-center gap-2 mt-3 text-xs text-white/70 cursor-pointer" data-testid={`admin-upscale-toggle-${img.key}`}>
                      <input type="checkbox" checked={!!upscaleFlags[img.key]}
                        onChange={(e) => setUpscaleFlags((f) => ({ ...f, [img.key]: e.target.checked }))}
                        className="accent-indigo-500" />
                      Escalar a 4K con IA al subir
                    </label>
                    <div className="flex items-center gap-2 mt-auto pt-4">
                      <label className="flex-1">
                        <input data-testid={`admin-upload-${img.key}`} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                          onChange={(e) => onUpload(img.key, e.target.files?.[0])} disabled={busyKey === img.key} />
                        <span className="cursor-pointer flex items-center justify-center gap-2 h-10 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white text-sm font-medium transition-colors">
                          <Upload className="h-4 w-4" /> Cambiar
                        </span>
                      </label>
                      <Button data-testid={`admin-reset-${img.key}`} onClick={() => onResetImage(img.key)} disabled={busyKey === img.key}
                        variant="outline" size="icon" className="h-10 w-10 rounded-full bg-white/5 border-white/15 text-white hover:bg-white/10 shrink-0" title="Restaurar original">
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Textos de la web</h1>
                <p className="text-white/55 mt-2">Edita los textos. Elige el idioma: "Original" es el texto maestro (se traduce solo a los demás). Cualquier idioma que edites a mano se respeta.</p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  data-testid="text-lang-select"
                  value={textLang}
                  onChange={(e) => changeTextLang(e.target.value)}
                  className="h-11 rounded-full bg-white/5 border border-white/15 text-white px-4 text-sm outline-none"
                >
                  {contentLangs.map((l) => (
                    <option key={l.code} value={l.code} className="bg-[#0a0a12]">{l.name}</option>
                  ))}
                </select>
                <Button data-testid="save-texts-button" onClick={saveTexts} disabled={savingText}
                  className="h-11 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-400 hover:to-violet-400 text-white border-0 gap-2 px-6">
                  {savingText ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Guardar</>}
                </Button>
              </div>
            </div>

            <div className="mt-8 space-y-10">
              {groups.map((group) => (
                <div key={group}>
                  <h2 className="text-lg font-semibold text-indigo-300 mb-4">{group}</h2>
                  <div className="grid md:grid-cols-2 gap-5">
                    {fields.filter((f) => f.group === group).map((f) => (
                      <div key={f.key} className="glass rounded-2xl p-5" data-testid={`text-field-${f.key}`}>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm text-white/70">{f.label}</label>
                          <button onClick={() => resetText(f.key)} className="text-white/40 hover:text-white" title="Restaurar original">
                            <RotateCcw className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {f.type === 'icon' ? (
                          textLang === 'base' ? (
                            <div className="flex items-center gap-3">
                              <span className="grid place-items-center h-11 w-11 rounded-xl bg-white/5 border border-white/15 shrink-0">
                                {(() => { const Ic = getIcon(draft[f.key]); return <Ic className="h-5 w-5 text-indigo-300" />; })()}
                              </span>
                              <select
                                data-testid={`icon-select-${f.key}`}
                                value={draft[f.key] ?? ""}
                                onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                                className="flex-1 h-11 rounded-xl bg-white/5 border border-white/15 text-white px-3 text-sm outline-none"
                              >
                                {ICON_NAMES.map((n) => (
                                  <option key={n} value={n} className="bg-[#0a0a12]">{n}</option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <p className="text-white/40 text-sm py-2">El icono se cambia en el idioma «Original».</p>
                          )
                        ) : f.type === 'raw' && textLang !== 'base' ? (
                          <p className="text-white/40 text-sm py-2">Este valor ({draft[f.key] || f.value}) es igual en todos los idiomas. Cámbialo en el idioma «Original».</p>
                        ) : f.multiline ? (
                          <Textarea
                            data-testid={`text-input-${f.key}`}
                            value={draft[f.key] ?? ""} rows={3}
                            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                            className="bg-white/5 border-white/15 text-white rounded-xl resize-none"
                          />
                        ) : (
                          <Input
                            data-testid={`text-input-${f.key}`}
                            value={draft[f.key] ?? ""}
                            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                            className="bg-white/5 border-white/15 text-white rounded-xl h-11"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
